import React, { createContext, Suspense, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomGreetingFilter from '../../../components/metricsScoreCard/CustomGreetingFilter';
import NotificationList from '../../../components/notificationList/NotificationList';
import { ShoutOutCarousel } from '../../coaching/toDoView/shoutOutCarousel/ShoutOutCarousel';
import {
    selectCurrentEmpid,
    selectCurrentUser,
    selectCurrentUserManagerLevel,
    selectIsAllowCoachingActionModel,
    selectIsPseudoSupervisor,
    selectIsUserinPercepta,
} from '../../../pages/login/authSlice';
import { NotificationItem } from '../../../components/notificationList/types';
import {
    useGetEmployeeNotificationsQuery,
    useUpdateNotificationMutation,
} from '../../header/notifications/notificationSlice';
import { getRole } from '../../../utils/roleUtils';
import { useGetShoutOutCarouselQuery } from '../../coaching/toDoView/toDoViewSlice';
import { TeamLeadDueTodos } from '../../../components/teamLeadDueTodos/TeamLeadDueTodos';
import './managerLandingPage.scss';
import { ApprovalLandingPage } from '../approvalLandingPage/ApprovalLandingPage';
import ServiceNowApprovalsList from '../../../components/serviceNowApprovalsList/ServiceNowApprovalsList';
import { ScorecardMetrics } from '../../../components/metricsScoreCard/ScorecardMetrics';
import { useGetCaManagerTlSearchQuery } from '../../correctiveActions/managerTlViewSearch/CaMangerTLView.slice';
import CorrectiveActionsGrid from '../../correctiveActions/correctiveActionsGrid/CorrectiveActionsGrid';

const ManagerCoachingActionPlanDashboard = React.lazy(
    () => import('../../coachingAction/managerCoachingActionPlanDashboard/ManagerCoachingActionPlanDashboard')
);

export const ManagerLandingContext = createContext({ viaPage: '' });

export const ManagerLandingPage = () => {
    const managerLandingContextValue = useMemo(() => ({ viaPage: 'ManagerLandingPage' }), []);
    const loggedUser = useSelector(selectCurrentUser);
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [UpdateNotification] = useUpdateNotificationMutation();
    const [inputMonth, setInputMonth] = useState(new Date().getMonth() + 1);
    const [inputYear, setInputYear] = useState(new Date().getFullYear());
    const userMngrRole = useSelector(selectCurrentUserManagerLevel);
    const isPseudoSupervisor = useSelector(selectIsPseudoSupervisor);
    const isAllowCoachingActionModel = useSelector(selectIsAllowCoachingActionModel);
    const isUserinPercepta = useSelector(selectIsUserinPercepta);
    const [scorecardVersion, setScorecardVersion] = useState<number>(0);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [scorecardName, setScorecardName] = useState<string>('');
    const [askNowCount, setAskNowCount] = useState(0);

    const { data: employeeNotifications, isFetching: employeeNotificationsFetching } =
        useGetEmployeeNotificationsQuery({
            employeeNumber: loggedEmpNum
        }, { skip: !loggedEmpNum });

    const { data: shoutOuts } = useGetShoutOutCarouselQuery({
        employeeNumber: loggedEmpNum,
        employeeRole: getRole(userMngrRole, isPseudoSupervisor),
    });
    const { data: employeeCorrectiveActionsData } = useGetCaManagerTlSearchQuery(
        {
            employeeNumber: loggedEmpNum,
            isApprovals: true,
        },
        {
            skip: !loggedEmpNum,
        }
    );

    useEffect(() => {
        if (!employeeNotificationsFetching) {
            if (employeeNotifications?.data?.length > 0) setNotifications(employeeNotifications.data);
            else setNotifications([]);
        }
    }, [employeeNotifications]);

    return (
        <ManagerLandingContext.Provider value={managerLandingContextValue}>
            <div className="landing-page-wrapper">
                <div className="rounded mt-3 border-0 homepagebody">
                    {/* Greeting and Filter */}
                    <div className="row mb-3">
                        <h3 className="homePageText">Welcome Back {loggedUser}!</h3>
                        <CustomGreetingFilter
                            inputmonth={inputMonth}
                            setInputMonth={setInputMonth}
                            inputyear={inputYear}
                            setInputYear={setInputYear}
                            scorecardVersionValue={setScorecardVersion}
                            calendarDate={calendarDate}
                            setCalendarDate={setCalendarDate}
                            scorecardNameValue={setScorecardName}
                        />
                    </div>

                    {/* First Row - Approvals & TeamLead Due Todos */}
                    <div className="row-two-columns first-row">
                        {/* Left Column - Approvals */}
                        <div className="column-left">
                            <div className="card shadow bg-body border-0 rounded-4">
                                <div className="notification-column ps-2" style={{ border: 'none' }}>

                                    {/* FLEX CONTAINER FOR HEADER ROW */}
                                    <div className="d-flex justify-content-between align-items-center pt-3">
                                        <div className="section-header">Approvals</div>

                                        {scorecardName !== '' && (
                                            <div
                                                className="section-header fst-italic"
                                                style={{ fontWeight: 400 }}
                                            >
                                                Specific for {scorecardName}
                                            </div>
                                        )}
                                    </div>
                                    <div className='approval-content-wrapper'>
                                        <ApprovalLandingPage
                                            inputMonth={inputMonth}
                                            inputYear={inputYear}
                                            scorecardVersionID={scorecardVersion}
                                        /></div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - TeamLead Due Todos */}
                        <div className="column-right">
                            <div className="card shadow bg-body border-0 rounded-4">
                                <div className="landing-page-ranking">
                                    <TeamLeadDueTodos viaPage="ManagerLandingPage" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Second Row - Metrics & Corrective Actions */}
                    <div className="row-two-columns mt-3">
                        {/* Left Column - Metrics */}
                        <div className="column-left-metrics">
                            <div className="card shadow bg-body border-0 rounded-4">
                                <div className="metrics-section">
                                    <ScorecardMetrics
                                        scorecardVersion={scorecardVersion}
                                        inputmonth={inputMonth}
                                        inputyear={inputYear}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Corrective Actions */}
                        <div className="column-right-corrective-actions">
                            <div className="card shadow bg-body border-0 rounded-4">
                                <div className="section-placeholder">
                                    <div className="section-header px-3 pt-3">Corrective Actions</div>
                                    <div className="placeholder-content">
                                        <div className="corrective-actions-table-wrapper">
                                            <CorrectiveActionsGrid
                                                actions={employeeCorrectiveActionsData?.data || []}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Coaching Action Plan Dashboard (New Coaching Model users) */}
                    {(isUserinPercepta === true || isAllowCoachingActionModel === true) && (
                        <div className="row mt-3">
                            <div
                                // className="card shadow bg-body border-0 rounded-4"
                                style={{ display: 'flex', flexDirection: 'column' }}
                            >
                                <Suspense fallback={<div className="p-3">Loading dashboard…</div>}>
                                    <ManagerCoachingActionPlanDashboard
                                        inputmonth={inputMonth}
                                        inputyear={inputYear}
                                    />
                                </Suspense>
                            </div>
                        </div>
                    )}

                    {/* Third Row - Notifications, ASKNow, and ShoutOut */}
                    <div className={`full-height-row-shoutout-notification mt-3 mb-3 ${askNowCount === 0 ? 'two-columns' : ''}`}>
                        {/* Notifications */}
                        <div
                            className="p-card card shadow bg-body border-0 ranking-list-container"
                            style={{ borderRadius: '25px' }}
                        >
                            <div className="section-header px-3 pt-3">Alerts & Notifications</div>
                            <div className="notification-content px-3">
                                <NotificationList
                                    gridLayout={askNowCount === 0}
                                    notifications={notifications}
                                    onNotificationRead={(id) => {
                                        const notification = {
                                            employeenumber: loggedEmpNum,
                                            isNotificationRead: true,
                                            notificationId: id,
                                            readAll: false,
                                        };
                                        UpdateNotification(notification)
                                            .unwrap()
                                            .then((res: any) => console.log('Marked as read', res))
                                            .catch((err: any) => console.error('Update failed', err));
                                    }}
                                />
                            </div>
                        </div>

                        {/* ASKNow Tickets - Only visible when askNowCount > 0 */}
                        {askNowCount > 0 && (
                            <div
                                className="p-card card shadow bg-body border-0 ranking-list-container"
                                style={{ borderRadius: '25px' }}
                            >
                                <div className="section-header px-3 pt-3">ASKNow Tickets</div>
                                <div className="asknow-content px-3">
                                    <ServiceNowApprovalsList setAskNowCount={setAskNowCount} />
                                </div>
                            </div>
                        )}

                        {/* ShoutOut  (RIGHT) */}
                        <div
                            className="bg-body border-0 shoutout-wrapper"
                            style={{ borderRadius: '25px', width: '100%' }}
                        >
                            <div className="card shadow bg-body border-0 rounded-4">
                                <div className="shoutout-content">
                                    <ShoutOutCarousel shoutOuts={shoutOuts?.data || []} via="agent_landingpage" />
                                </div>
                            </div>
                        </div>

                        {/* Hidden ServiceNow component to get count when askNowCount === 0 */}
                        {askNowCount === 0 && (
                            <div style={{ display: 'none' }}>
                                <ServiceNowApprovalsList setAskNowCount={setAskNowCount} />
                            </div>
                        )}
                    </div>
                </div>
            </div >
        </ManagerLandingContext.Provider >
    );
};